import * as React from "react";
import { GetFaQsByEventQuery } from "@src/@types/api.d";
import { useFaqList } from "@src/hooks/useFaqList";
import { motion } from "framer-motion";
import Image from "next/image";

import defaultImage from "../../../../public/images/image20.jpeg";
import bgImg from "../../../../public/images/t19bg.png";

export type ITemplate23FAQ = {
  eventId: number;
  faqData?: GetFaQsByEventQuery;
  loading?: boolean;
  sectionImage?: string;
};

interface FAQCardProps {
  faq: GetFaQsByEventQuery["getFAQsByEvent"]["data"][number];
}

const FAQCard: React.FC<FAQCardProps> = ({ faq }) => (
  <div className="p-4 flex flex-col justify-center items-center text-center text-[#FCE2B0] gap-1">
    <h3 className="text-xl font-semibold font-ebGaramond tracking-wider uppercase max-w-[85%] sm:max-w-2xl">
      {faq.question}
    </h3>
    <p className="text-lg font-ebGaramond max-w-[85%] sm:max-w-2xl text-[#FFF3DB] whitespace-pre-line">
      {faq.answer}
    </p>
  </div>
);

const FAQ: React.FC<ITemplate23FAQ> = ({ eventId, faqData, sectionImage }) => {
  const {
    faqItems,
    paginatedFaqItems,
    totalPages,
    goToPreviousPage,
    goToNextPage,
  } = useFaqList({
    faqData,
    itemsPerPage: 3,
    wrapPagination: true,
  });

  if (!faqItems.length) return null;

  return (
    <div className="w-full flex flex-col lg:flex-row">
      {/* Content Section - Takes natural height on mobile, centered on desktop */}
      <div className="w-full lg:flex-1 lg:h-full relative order-1 lg:order-2">
        {/* Background image */}
        <div className="absolute inset-0">
          <Image
            src={bgImg}
            alt="background"
            layout="fill"
            className="object-cover object-center"
          />
        </div>

        <div className="relative z-10 w-full h-full flex flex-col justify-center items-center py-12 lg:py-24 px-6 lg:px-10">
          <motion.h1
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-3xl sm:text-4xl lg:text-5xl font-ntFab text-[#FCE2B0] mb-8 lg:mb-12"
          >
            Q & A
          </motion.h1>

          <div className="flex flex-col gap-6 lg:gap-8 max-w-4xl w-full">
            {paginatedFaqItems.map((faq) => (
              <FAQCard key={faq.id} faq={faq} />
            ))}
          </div>

          {/* Pagination Controls (styled similarly to template16) */}
          {totalPages > 1 && (
            <div className="flex justify-center items-center gap-3 mt-8">
              <button
                onClick={goToPreviousPage}
                className="h-10 w-10 rounded-full bg-[rgba(42,46,50,1)] text-white border-2 border-white text-xl font-semibold"
                aria-label="Previous page"
              >
                ‹
              </button>

              <button
                onClick={goToNextPage}
                className="h-10 w-10 rounded-full bg-[rgba(42,46,50,1)] text-white border-2 border-white text-xl font-semibold"
                aria-label="Next page"
              >
                ›
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Image Section - Fixed height on mobile, follows content */}
      <div className="w-full h-[50vh] lg:flex-1 lg:h-full order-2 lg:order-1 lg:hidden">
        <div className="relative w-full h-full">
          <Image
            loading="eager"
            layout="fill"
            src={sectionImage || defaultImage}
            alt="romantic-quote-image"
            className="object-cover object-center lg:object-top"
            sizes="(max-width: 1024px) 100vw, 50vw"
          />
        </div>
      </div>
    </div>
  );
};

export default FAQ;
